<template>
  <div class="job-architecture-page">
    <TopBarForDrawer>
      <template #breadcrumbs>
        <router-link
          :to="breadcrumbBackTo"
          class="breadcrumb-back-link text-body-2"
        >
          {{ breadcrumbBackLabel }}
        </router-link>
        <span class="breadcrumb-separator text-body-2">/</span>
        <span class="text-body-2 text-medium-emphasis">Compare profiles</span>
      </template>
      <template #page-title>
        <TopBarForDrawerTitle
          :page-title="route.meta.title"
          :page-description="pageDescription"
        />
      </template>
      <template #summaries>
        <h3
          v-if="profiles.length > 0"
          class="text-body-2 text-textDarkest text-right"
        >
          Job Profiles
          <span class="text-textBrand">{{ profiles.length }}</span>
        </h3>
      </template>
    </TopBarForDrawer>

    <div class="job-architecture-controls-row">
      <div class="job-architecture-filters-left">
        <RowsFilterDropdown
          v-if="tableControls"
          :fields="tableControls.orderedFieldsForRows || tableControls.allFields"
          :visible-ids="tableControls.visibleFieldIdsArray"
          @toggle-field="tableControls.toggleFieldVisibility"
        />
        <AddJobProfileSearch
          :existing-ulids="existingHrisCompareUlids"
          @add-profile="handleAddHrisProfile"
        />
        <AddDraftJobProfileSearch
          :existing-ulids="existingDraftCompareUlids"
          @add-profile="handleAddDraftProfile"
        />
      </div>
      <div class="job-architecture-filters-right">
        <v-btn
          variant="outlined"
          rounded="lg"
          class="filter-style-btn"
          :disabled="!canDownloadCsv"
          @click="handleDownloadCsv"
        >
          <v-icon icon="mdi-download" start />
          Download CSV
        </v-btn>
      </div>
    </div>

    <template v-if="noSelection">
      <p class="text-medium-emphasis pa-4">
        No job profiles selected. Select job profiles in the
        <router-link :to="breadcrumbBackTo" class="breadcrumb-back-link">
          {{ breadcrumbBackLabel.toLowerCase() }}
        </router-link>
        and click “Compare selected”.
      </p>
    </template>

    <template v-else>
      <JobProfileComparisonTable
        v-if="fieldSettingsLoaded && profiles.length > 0"
        :profiles="profiles"
        :field-keys-order="fieldKeysOrder"
        :hidden-field-keys="hiddenFieldKeys"
        :levels="levels"
        @update:controls="tableControls = $event"
        @remove-profile="handleRemoveProfile"
      />
      <p
        v-else-if="profilesLoading"
        class="text-medium-emphasis pa-4"
      >
        Loading profiles…
      </p>
      <p
        v-else-if="profilesError"
        class="text-medium-emphasis pa-4"
      >
        Failed to load profiles. Please try again.
      </p>
      <p
        v-else
        class="text-medium-emphasis pa-4"
      >
        No profile data could be loaded for the selected profiles.
      </p>
    </template>
  </div>
</template>

<script setup>
import { format } from 'date-fns';
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { getProfilesByUlids } from '@/api/architecture';
import { getDraftProfilesByUlids } from '@/api/jobProfile';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useJobProfileComparisonFieldSettings } from '@/composables/useJobProfileComparisonFieldSettings';
import {
  normalizeDraftProfileForCompare,
  normalizeHrisProfileForCompare,
} from '@/stores/jobArchitecture/helpers';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import { useAppAuthData } from '@/stores/app/authData';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';
import { resolveCompareBreadcrumb } from '@/helpers/jobProfileCompareFrom';
import { useTracking } from '@/stores/tracking/tracking';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import JobProfileComparisonTable from '@/components/jobArchitecture/JobProfileComparisonTable.vue';
import AddJobProfileSearch from '@/components/jobArchitecture/AddJobProfileSearch.vue';
import AddDraftJobProfileSearch from '@/components/jobArchitecture/AddDraftJobProfileSearch.vue';
import RowsFilterDropdown from '@/components/jobArchitecture/RowsFilterDropdown.vue';

const route = useRoute();
const router = useRouter();

const { authData } = storeToRefs(useAppAuthData());
const jobArchitectureStore = useJobArchitecture();
const { setCompareSelectionUlids, loadTree } = jobArchitectureStore;
const { treeData } = storeToRefs(jobArchitectureStore);

const { setLoading } = useAppLoading();
const { listLoading: profilesLoading } = storeToRefs(useAppLoading());

const { selectedProfiles: selectedHrisProfiles } = storeToRefs(useJobProfiles());
const { selectedProfiles: selectedDraftProfiles } = storeToRefs(useJobProfileDrafts());

const tableControls = ref(null);
const profiles = ref([]);
const profilesError = ref(false);
const {
  levels, fieldKeysOrder, hiddenFieldKeys, fieldSettingsLoaded,
} = useJobProfileComparisonFieldSettings();

const parseUlidsQuery = (raw) => {
  if (!raw) return [];

  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
};

const hrisProfileUlids = computed(() => parseUlidsQuery(route.query?.profiles));

const draftProfileUlids = computed(() => parseUlidsQuery(route.query?.draftProfiles));

const noSelection = computed(() => (
  hrisProfileUlids.value.length === 0 && draftProfileUlids.value.length === 0
));

const compareBreadcrumb = computed(() => resolveCompareBreadcrumb({
  from: route.query?.from,
  fromId: route.query?.fromId,
  hrisUlids: hrisProfileUlids.value,
  draftUlids: draftProfileUlids.value,
  hrisName: authData.value.hrisName,
}));

const breadcrumbBackTo = computed(() => compareBreadcrumb.value.backTo);
const breadcrumbBackLabel = computed(() => compareBreadcrumb.value.label);

const existingHrisCompareUlids = computed(() => profiles.value
  .filter((profile) => profile.source !== 'draft')
  .map((profile) => profile.ulid));

const existingDraftCompareUlids = computed(() => profiles.value
  .filter((profile) => profile.source === 'draft')
  .map((profile) => profile.ulid));

const pageDescription = computed(() => pageDescriptions()[route.name] || '');

const csvDownloadFileName = computed(() => `job-profiles-compare-${format(new Date(), 'MM-dd-yyyy')}.csv`);

const canDownloadCsv = computed(() => (
  !profilesLoading.value
  && profiles.value.length > 0
  && tableControls.value?.hasExportableResults === true
));

const handleDownloadCsv = () => {
  useTracking().jaMatrixDownloadedCsv();

  tableControls.value?.downloadCsv?.({ fileName: csvDownloadFileName.value });
};

const replaceCompareQuery = (hrisUlids, draftUlids) => {
  const query = { ...route.query };

  if (hrisUlids.length) {
    query.profiles = hrisUlids.join(',');
  } else {
    delete query.profiles;
  }

  if (draftUlids.length) {
    query.draftProfiles = draftUlids.join(',');
  } else {
    delete query.draftProfiles;
  }

  router.replace({ query });
};

const syncCompareSelectionUlids = () => {
  setCompareSelectionUlids(profiles.value.map((profile) => profile.ulid));
};

const loadProfiles = async () => {
  const hrisUlids = hrisProfileUlids.value;
  const draftUlids = draftProfileUlids.value;

  if (hrisUlids.length === 0 && draftUlids.length === 0) {
    profiles.value = [];
    profilesError.value = false;
    tableControls.value = null;

    setCompareSelectionUlids([]);

    return;
  }

  setLoading({ listLoading: true });

  profilesError.value = false;

  try {
    const [hrisData, draftData] = await Promise.all([
      hrisUlids.length ? getProfilesByUlids(hrisUlids) : Promise.resolve([]),
      draftUlids.length ? getDraftProfilesByUlids(draftUlids) : Promise.resolve([]),
    ]);

    const hrisByUlid = new Map(
      (hrisData || [])
        .map((profile) => normalizeHrisProfileForCompare(profile))
        .filter(Boolean)
        .map((profile) => [profile.ulid, profile]),
    );
    const draftByUlid = new Map(
      (draftData || [])
        .map((profile) => normalizeDraftProfileForCompare(profile))
        .filter(Boolean)
        .map((profile) => [profile.ulid, profile]),
    );

    const orderedHris = hrisUlids.map((ulid) => hrisByUlid.get(ulid)).filter(Boolean);
    const orderedDrafts = draftUlids.map((ulid) => draftByUlid.get(ulid)).filter(Boolean);

    profiles.value = [...orderedHris, ...orderedDrafts];

    if (!profiles.value.length) {
      tableControls.value = null;
    }

    syncCompareSelectionUlids();
  } catch (err) {
    useAppSnackbar().addErrorSnackbar({
      text: `Job profiles load error: ${err.message || err}`,
    });

    profiles.value = [];
    profilesError.value = true;
    tableControls.value = null;
    setCompareSelectionUlids([]);
  } finally {
    setLoading({ listLoading: false });
  }
};

const removeProfilesFromHrisLibraryStore = (ulid) => {
  if (!selectedHrisProfiles.value.has(ulid)) return;
  selectedHrisProfiles.value.delete(ulid);
};

const removeProfilesFromDraftLibraryStore = (ulid) => {
  if (!selectedDraftProfiles.value.has(ulid)) return;
  selectedDraftProfiles.value.delete(ulid);
};

const handleRemoveProfile = (ulid) => {
  const removed = profiles.value.find((profile) => profile.ulid === ulid);
  const isDraft = removed?.source === 'draft';

  profiles.value = profiles.value.filter((profile) => profile.ulid !== ulid);

  syncCompareSelectionUlids();

  if (isDraft) {
    removeProfilesFromDraftLibraryStore(ulid);
  } else {
    removeProfilesFromHrisLibraryStore(ulid);
  }

  const nextHris = profiles.value
    .filter((profile) => profile.source !== 'draft')
    .map((profile) => profile.ulid);
  const nextDrafts = profiles.value
    .filter((profile) => profile.source === 'draft')
    .map((profile) => profile.ulid);

  replaceCompareQuery(nextHris, nextDrafts);
};

const addProfilesToHrisLibraryStore = (ulid) => {
  if (selectedHrisProfiles.value.has(ulid)) return;
  selectedHrisProfiles.value.add(ulid);
};

const addProfilesToDraftLibraryStore = (ulid) => {
  if (selectedDraftProfiles.value.has(ulid)) return;
  selectedDraftProfiles.value.add(ulid);
};

const handleAddHrisProfile = async (ulid) => {
  if (profiles.value.some((profile) => profile.ulid === ulid)) return;

  useTracking().jaMatrixAddedJobProfile();

  addProfilesToHrisLibraryStore(ulid);

  setLoading({ listLoading: true });

  try {
    const data = await getProfilesByUlids([ulid]);
    const newProfile = normalizeHrisProfileForCompare(data?.[0]);

    if (!newProfile) return;

    profiles.value = [newProfile, ...profiles.value];

    syncCompareSelectionUlids();

    const nextHris = profiles.value
      .filter((profile) => profile.source !== 'draft')
      .map((profile) => profile.ulid);
    const nextDrafts = profiles.value
      .filter((profile) => profile.source === 'draft')
      .map((profile) => profile.ulid);

    replaceCompareQuery(nextHris, nextDrafts);
  } catch (err) {
    useAppSnackbar().addErrorSnackbar({
      text: `Could not add profile: ${err.message || err}`,
    });
  } finally {
    setLoading({ listLoading: false });
  }
};

const handleAddDraftProfile = async (ulid) => {
  if (profiles.value.some((profile) => profile.ulid === ulid)) return;

  useTracking().jaMatrixAddedJobProfile();

  addProfilesToDraftLibraryStore(ulid);

  setLoading({ listLoading: true });

  try {
    const data = await getDraftProfilesByUlids([ulid]);
    const newProfile = normalizeDraftProfileForCompare(data?.[0]);

    if (!newProfile) return;

    // Keep drafts after HRIS columns when both are present.
    const hrisProfiles = profiles.value.filter((profile) => profile.source !== 'draft');
    const draftProfiles = profiles.value.filter((profile) => profile.source === 'draft');

    profiles.value = [...hrisProfiles, newProfile, ...draftProfiles];

    syncCompareSelectionUlids();

    const nextHris = hrisProfiles.map((profile) => profile.ulid);
    const nextDrafts = [ulid, ...draftProfiles.map((profile) => profile.ulid)];

    replaceCompareQuery(nextHris, nextDrafts);
  } catch (err) {
    useAppSnackbar().addErrorSnackbar({
      text: `Could not add profile: ${err.message || err}`,
    });
  } finally {
    setLoading({ listLoading: false });
  }
};

watch(
  () => [route.query.profiles, route.query.draftProfiles],
  () => {
    loadProfiles();
  },
  { immediate: true },
);

onMounted(async () => {
  useTracking().trackPageLoad({ page: 'Job Architecture Compare: Open' });

  if (!treeData.value) {
    await loadTree();
  }
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 1243px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 24px 16px;
}

:deep(.topbar) {
  padding-left: 0;
  padding-right: 0;
}

:deep(.topbar-breadcrumbs) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.job-architecture-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.breadcrumb-back-link {
  color: @text-brand;
  text-decoration: none;
}

.breadcrumb-back-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: @grey-500;
}

.job-architecture-filters-left,
.job-architecture-filters-right {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-style-btn {
  height: 40px !important;
  min-height: 40px !important;
  background-color: @bg-default !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
  text-transform: none;
}
</style>
